import { getOrderByCode, orderAndPay, pay, type OnOrder, type PayResponse } from 'services/api/pay';
import { startAsaasCheckout, type AsaasStartResult } from './checkout';
import { getAsaasHandoff } from './handoff';
import { prepareAsaasOrderAttempt, releaseAsaasIdempotencyKey } from './idempotency';
import { findPendingAsaasPaymentByOrderCode } from './pending';
import { buildAsaasPaymentResultUrl } from './returnHandoff';
import { deriveAsaasPaymentSource } from './source';
import { ASAAS_PAY_TYPE, type AsaasOrderInfo } from './types';
import { getAsaasPaymentWatcher, getAsaasPanelSource, isAsaasContinuePayBlocked } from './watcher';

export async function createAsaasOrderAndCheckout<T extends Parameters<OnOrder>[0]>(
  info: T,
  createOrder: OnOrder = orderAndPay,
) {
  const source = deriveAsaasPaymentSource(info);
  const signature = JSON.stringify([
    info.packageType,
    info.orderAction,
    info.packageId,
    info.currency,
    info.discountTotalPrice,
    'regionPackageId' in info ? info.regionPackageId : undefined,
    'durationNum' in info ? info.durationNum : undefined,
    'accountNum' in info ? info.accountNum : undefined,
    'subscription' in info ? info.subscription : undefined,
    'handleNum' in info ? info.handleNum : undefined,
    'trafficPackageType' in info ? info.trafficPackageType : undefined,
    'orderNumber' in info ? info.orderNumber : undefined,
  ]);
  const attempt = prepareAsaasOrderAttempt(`market-${source}`, signature);
  const request = {
    ...info,
    idempotent: attempt.idempotent,
    payResultUrl: attempt.payResultUrl,
    ...('subscription' in info && info.subscription === true
      ? { cancelResultUrl: attempt.payResultUrl }
      : {}),
  };
  const response = await createOrder(request);
  const result = await startAsaasCheckout({ ...info, ...response }, response, source, attempt);
  releaseAsaasIdempotencyKey(attempt.idempotencyScope);
  return result;
}

function createPaymentAttempt(
  orderCode: string,
  language: General.SystemLanguage,
  returnId: string,
): Promise<PayResponse> {
  return pay({
    idempotent: String(Date.now()),
    orderCode,
    language,
    payResultUrl: buildAsaasPaymentResultUrl(returnId, getAsaasHandoff()),
  });
}

export type ContinueAsaasResult<T extends AsaasOrderInfo> =
  | AsaasStartResult<T>
  | { outcome: 'blocked' };

export async function continueAsaasCheckout<T extends AsaasOrderInfo>(
  order: T,
  language: General.SystemLanguage,
): Promise<ContinueAsaasResult<T>> {
  const phase = getAsaasPaymentWatcher(order.orderCode)?.getSnapshot().phase;
  if (phase && isAsaasContinuePayBlocked(phase)) return { outcome: 'blocked' };
  const returnId = crypto.randomUUID();
  const response = await createPaymentAttempt(order.orderCode, language, returnId);
  const latestPhase = getAsaasPaymentWatcher(order.orderCode)?.getSnapshot().phase;
  if (latestPhase && isAsaasContinuePayBlocked(latestPhase)) return { outcome: 'blocked' };
  const source =
    findPendingAsaasPaymentByOrderCode(order.orderCode)?.source ??
    getAsaasPanelSource(order.orderCode) ??
    deriveAsaasPaymentSource(order);
  return startAsaasCheckout(
    { ...order, payType: ASAAS_PAY_TYPE, currency: response.currency || order.currency },
    response,
    source,
    { returnId },
  );
}

export type RepayAsaasBillingResult<T extends AsaasOrderInfo> =
  | AsaasStartResult<T>
  | { outcome: 'not-asaas' }
  | { outcome: 'aborted'; reason: 'prefetch-failed' | 'channel-unknown'; error?: Error };

export async function repayAsaasBillingOrder<T extends AsaasOrderInfo>(
  order: T,
  language: General.SystemLanguage,
): Promise<RepayAsaasBillingResult<T>> {
  try {
    const detail = await getOrderByCode(order.orderCode);
    if (detail.paymentType === undefined || detail.paymentType === null) {
      return { outcome: 'aborted', reason: 'channel-unknown' };
    }
    if (detail.paymentType !== ASAAS_PAY_TYPE) return { outcome: 'not-asaas' };
  } catch (error) {
    return {
      outcome: 'aborted',
      reason: 'prefetch-failed',
      error: error instanceof Error ? error : new Error(String(error)),
    };
  }
  const returnId = crypto.randomUUID();
  const response = await createPaymentAttempt(order.orderCode, language, returnId);
  return startAsaasCheckout(
    { ...order, payType: ASAAS_PAY_TYPE, currency: response.currency || order.currency },
    response,
    'billing',
    { returnId },
  );
}

export * from './checkout';
export { clearAsaasIdempotencyKeys } from './idempotency';
export * from './returnHandoff';
export {
  clearPendingAsaasPayment,
  findPendingAsaasPaymentByOrderCode,
  isAsaasPendingPaymentExpired,
  readPendingAsaasPayment,
  resolveAsaasReturnRestore,
  savePendingAsaasPayment,
} from './pending';
export * from './types';
export * from './watcher';
